import React, {useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import {AddLocationAlt, EditLocationAlt, PinDrop, Refresh, SwapVert, TravelExplore} from '@mui/icons-material';
import PageTitle from './layout/PageTitle';
import Loader from './layout/loading/loader';
import {toastHelper} from '../../utils/toast-helper';
import {helper} from '../../utils/helper';
import {extractId} from '../../utils/apiResource';
import {useGetCampaignQuery} from '../../modules/campaigns/campaigns.api';
import {useGetPatrimoineDetailQuery, useUpdatePlanTourneeMutation} from '../../modules/patrimoines/patrimoines.api';
import {parseTourneeCsv} from '../../utils/tourneeUpdate/parseTourneeCsv';
import {buildPlanTourneePayload, diffConteneurs} from '../../utils/tourneeUpdate/diffConteneurs';
import {readCsvFile} from '../../utils/tourneeUpdate/readCsvFile';

/**
 * TOUR-02 — Mise à jour du plan de tournée par fichier CSV.
 * Floorplan : Object page · Mobilité : Bureau · Rôle : EXPLOITANT.
 *
 * Deux colonnes : à gauche l'ordre actuel du patrimoine, à droite le fichier importé
 * (`Nomenclature;Adresse;Latitude;Longitude`). Les écarts (ordre, adresse, coordonnées
 * avec distance, absents, nouveaux) sont mis en évidence sur les lignes concernées.
 * La validation exige que le fichier soit une permutation exacte du patrimoine (aucun
 * absent, aucun nouveau — à régler dans le patrimoine, puis « Actualiser ») et applique
 * alors tous les écarts. Aucun géocodage, aucune suppression.
 */

const Item = styled(Paper)(({theme, bgcolor, color}) => ({
    backgroundColor: bgcolor,
    padding: theme.spacing(.5),
    textAlign: 'center',
    color: color,
    whiteSpace: 'nowrap',
    ...theme.typography.subtitle2,
}));

const ROW_ABSENT = {backgroundColor: '#B22222', color: '#FFF'};
const ROW_CHANGED = {backgroundColor: 'var(--thm-black)', color: '#FFF'};
const ROW_NEW = {backgroundColor: 'var(--thm-base)', color: '#FFF'};

/**
 * Seuil d'AFFICHAGE des déplacements (R9 amendée 2026-09-02) : un écart de coordonnées
 * ≤ 5 m est appliqué comme les autres mais n'est pas mis en évidence (bruit GPS).
 */
const COORDS_DISPLAY_THRESHOLD_M = 5;
const coordsVisible = (e) => !!e.coords && (e.coords.distanceM == null || e.coords.distanceM > COORDS_DISPLAY_THRESHOLD_M);
/** Écart visible = ordre, adresse, ou coords au-dessus du seuil d'affichage. */
const hasVisibleChange = (e) => !!(e.ordre || e.adresse || coordsVisible(e));

/**
 * Pastille d'écart : icône + texte court, largeur fixe par type pour que les colonnes
 * s'alignent d'une ligne à l'autre. Le détail (ancien → nouveau) est dans le tooltip.
 */
const TAG_WIDTH = {ordre: 86, adresse: 36, coords: 92};
const TAG_COLOR = {ordre: 'var(--thm-black-light3)', adresse: 'var(--thm-black-light3)', coords: 'var(--thm-orange)'};

function Tag({kind, icon, label, title}) {
    const body = (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={.5}
               sx={{
                   width: TAG_WIDTH[kind], height: 26, borderRadius: 1, px: .5,
                   bgcolor: TAG_COLOR[kind], color: '#FFF', fontSize: 13, fontWeight: 600,
                   whiteSpace: 'nowrap', cursor: title ? 'help' : 'default',
               }}>
            {icon}
            {label && <span>{label}</span>}
        </Stack>
    );
    return title ? <Tooltip title={title} arrow placement="left">{body}</Tooltip> : body;
}

/** Emplacement vide de même largeur : garde l'alignement en colonnes. */
const TagSlot = ({kind}) => <Box sx={{width: TAG_WIDTH[kind], height: 26}}/>;

const fmtCoords = (c) => (c ? `${c.lat}, ${c.lng}` : '—');

const formatLine = (ordre, nomenclature, adresse) => `${ordre ?? '—'} : ${nomenclature}${adresse ? ' - ' + adresse : ''}`;

function CampaignUpdate() {
    const {id: campaignId} = useParams();
    const inputFileRef = useRef(null);

    const [fileName, setFileName] = useState(null);
    const [parsed, setParsed] = useState(null);
    const [onlyChanges, setOnlyChanges] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const {data: campaign, isLoading: campaignLoading, isError: campaignError} = useGetCampaignQuery(campaignId, {skip: !campaignId});
    const patrimoineId = extractId(campaign?.patrimoine);
    const {data: patrimoine, isLoading: patrimoineLoading, isFetching: patrimoineFetching, isError: patrimoineError, refetch: refetchPatrimoine} = useGetPatrimoineDetailQuery(patrimoineId, {skip: !patrimoineId});
    const [updatePlanTournee, {isLoading: isUpdating}] = useUpdatePlanTourneeMutation();

    const conteneurs = useMemo(() => patrimoine?.conteneurs ?? [], [patrimoine]);
    const conteneursOrdonnes = useMemo(
        () => [...conteneurs].sort((a, b) => (a.ordreCsv ?? 0) - (b.ordreCsv ?? 0)),
        [conteneurs],
    );
    const nbConteneurs = useMemo(() => conteneurs.reduce((sum, c) => sum + (Number(c.quantite) || 0), 0), [conteneurs]);

    const diff = useMemo(() => (parsed ? diffConteneurs(conteneurs, parsed.rows) : null), [parsed, conteneurs]);
    const entryByNomenclature = useMemo(() => {
        const m = new Map();
        diff?.entries.forEach((e) => { if (e.conteneur) m.set(e.row.nomenclature, e); });
        return m;
    }, [diff]);
    const absentByNomenclature = useMemo(() => {
        const m = new Map();
        diff?.absents.forEach((a) => m.set(a.conteneur.nomenclature, a));
        return m;
    }, [diff]);
    const nbNouveauxConteneurs = useMemo(() => {
        if (!diff) return 0;
        return diff.entries.reduce((sum, e) => sum + (e.conteneur ? Number(e.conteneur.quantite) || 0 : 0), 0);
    }, [diff]);

    const hasBlockingErrors = (parsed?.errors?.length ?? 0) > 0;
    const notReconciled = !!diff && !diff.isReconciled; // R15 : absents / nouveaux bloquent
    const canValidate = !!diff && !hasBlockingErrors && !notReconciled && diff.hasChanges && !isUpdating;

    const resetImport = () => {
        if (inputFileRef.current) inputFileRef.current.value = null;
        setFileName(null);
        setParsed(null);
    };

    const handleFile = async (file) => {
        if (!file) return;
        try {
            const text = await readCsvFile(file);
            setFileName(file.name);
            setParsed(parseTourneeCsv(text));
        } catch (e) {
            toastHelper.handleError(e?.toString?.() ?? 'read error', 'Impossible de lire le fichier.');
            resetImport();
        }
    };

    const submit = async () => {
        if (!diff || !patrimoineId) return;
        const payload = buildPlanTourneePayload(diff);
        try {
            const result = await updatePlanTournee({patrimoineId, conteneurs: payload.conteneurs}).unwrap();
            setConfirmOpen(false);
            resetImport();
            toastHelper.displaySuccess(`Plan de tournée mis à jour : ${result?.misAJour ?? payload.conteneurs.length} conteneur(s).`);
        } catch (err) {
            setConfirmOpen(false);
            const violations = err?.data?.violations;
            if (Array.isArray(violations) && violations.length > 0) {
                toastHelper.handleError(null, `Refusé par le serveur : ${violations[0].propertyPath} — ${violations[0].message}`);
            } else {
                toastHelper.handleError(err?.toString?.() ?? 'error', 'Oops, la mise à jour a échoué. Rien n\'a été modifié.');
            }
        }
    };

    if (campaignLoading || patrimoineLoading) {
        return <Loader/>;
    }

    const noPatrimoine = !campaignError && campaign && !patrimoineId;
    const emptyPatrimoine = !!patrimoine && conteneurs.length === 0;
    const importDisabled = campaignError || patrimoineError || noPatrimoine || emptyPatrimoine;

    // --- Colonne gauche : ordre actuel ----------------------------------------------------
    const leftRowStyle = (c) => {
        if (!diff) return undefined;
        if (absentByNomenclature.has(c.nomenclature)) return ROW_ABSENT;
        const e = entryByNomenclature.get(c.nomenclature);
        if (e?.ordre) return ROW_CHANGED;
        return undefined;
    };
    const leftVisible = conteneursOrdonnes.filter((c) => {
        if (!onlyChanges || !diff) return true;
        const e = entryByNomenclature.get(c.nomenclature);
        return absentByNomenclature.has(c.nomenclature) || (e ? hasVisibleChange(e) : false);
    });

    // --- Colonne droite : fichier -----------------------------------------------------------
    const rightRowStyle = (e) => {
        if (e.status === 'unknown') return ROW_NEW;
        if (hasVisibleChange(e)) return ROW_CHANGED;
        return undefined;
    };
    const rightVisible = diff ? diff.entries.filter((e) => !onlyChanges || e.status === 'unknown' || hasVisibleChange(e)) : [];

    return (
        <>
            <PageTitle title={'Mettre à jour le plan de tournée'} icon={<TravelExplore/>}/>

            <Paper sx={{padding: '.5rem 1rem'}}>
                {(campaignError || patrimoineError) && (
                    <Alert severity="error" sx={{mb: 2}}>Impossible de charger la tournée ou son patrimoine.</Alert>
                )}
                {noPatrimoine && (
                    <Alert severity="warning" sx={{mb: 2}}>Cette tournée n'a pas de patrimoine rattaché : aucun import possible.</Alert>
                )}
                {emptyPatrimoine && (
                    <Alert severity="warning" sx={{mb: 2}}>Le patrimoine de cette tournée ne contient aucun conteneur : aucun import possible.</Alert>
                )}

                <Grid container size={12} spacing={2}>
                    {/* ============================ Colonne gauche ============================ */}
                    <Grid size={{xs: 12, md: 6}}>
                        <Card variant="outlined">
                            <Box sx={{p: 2}}>
                                <Typography gutterBottom variant="h5" component="div">
                                    {campaign?.libelle ?? '—'}
                                </Typography>
                                <Typography color="text.secondary" variant="body2">
                                    {campaign?.chauffeur?.libelle ? `par ${campaign.chauffeur.libelle}` : ''}
                                    {campaign?.equipier?.libelle ? ` et ${campaign.equipier.libelle}` : ''}
                                    {patrimoine?.libelle ? ` · ${patrimoine.libelle}` : ''}
                                </Typography>
                            </Box>
                            <Divider/>
                            <Box sx={{p: 2}}>
                                <Typography variant="subtitle1" mb={1}>Informations</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Chip label={`${conteneurs.length} points à laver`} size="small"/>
                                    <Chip label={`${nbConteneurs} conteneurs`} size="small"/>
                                </Stack>
                            </Box>
                            <Divider/>
                            <Box sx={{p: 2}}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography gutterBottom variant="subtitle1" mb={1}>Ordre actuel</Typography>
                                    {diff && (
                                        <FormControlLabel
                                            control={<Switch size="small" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)}/>}
                                            label={<Typography variant="body2">Écarts seulement</Typography>}
                                        />
                                    )}
                                </Stack>
                                <List dense>
                                    {leftVisible.map((c) => {
                                        const absent = absentByNomenclature.get(c.nomenclature);
                                        const e = entryByNomenclature.get(c.nomenclature);
                                        return (
                                            <ListItem key={c.id ?? c.nomenclature} divider sx={leftRowStyle(c)}
                                                      secondaryAction={
                                                          <Stack direction="row" spacing={.5}>
                                                              {absent && (
                                                                  <Item bgcolor="#FFF" color="var(--thm-red)">Absent</Item>
                                                              )}
                                                              {e?.ordre && (
                                                                  <Tag kind="ordre" icon={<SwapVert fontSize="small"/>} label={`${e.ordre.from ?? '—'} → ${e.ordre.to}`} title={`Ordre ${e.ordre.from ?? '—'} → ${e.ordre.to}`}/>
                                                              )}
                                                          </Stack>
                                                      }>
                                                <ListItemText primary={formatLine(c.ordreCsv, c.nomenclature, c.adresse)}/>
                                            </ListItem>
                                        );
                                    })}
                                    {leftVisible.length === 0 && (
                                        <ListItem><ListItemText secondary={onlyChanges ? 'Aucun écart.' : 'Aucun conteneur.'}/></ListItem>
                                    )}
                                </List>
                            </Box>
                        </Card>
                    </Grid>

                    {/* ============================ Colonne droite ============================ */}
                    <Grid size={{xs: 12, md: 6}}>
                        <Card variant="outlined">
                            <Box sx={{p: 2}}>
                                <Typography gutterBottom variant="h5" component="div">
                                    Importer nouvel ordre de tournée
                                </Typography>
                                <Typography color="text.secondary" variant="body2">
                                    fichier CSV « ; » avec <code>Nomenclature;Adresse;Latitude;Longitude</code> dans le nouvel ordre
                                    (coordonnées facultatives)
                                </Typography>
                            </Box>
                            <Divider/>
                            <Box sx={{p: 2}}>
                                <Typography variant="subtitle1" mb={1}>Informations</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip label={`${parsed?.rows.length ?? 0} points à laver`} size="small"/>
                                    <Chip label={`${nbNouveauxConteneurs} conteneurs`} size="small"/>
                                    {diff && (
                                        <>
                                            <Chip size="small" sx={ROW_CHANGED} label={`${diff.counts.ordre} ordre`}/>
                                            <Chip size="small" sx={ROW_CHANGED} label={`${diff.counts.adresse} adresse`}/>
                                            <Chip size="small" sx={ROW_CHANGED} label={`${diff.counts.coords} coordonnées`}/>
                                            <Chip size="small" sx={diff.counts.unknown ? ROW_NEW : undefined} label={`${diff.counts.unknown} nouveau(x)`}/>
                                            <Chip size="small" sx={diff.counts.absents ? ROW_ABSENT : undefined} label={`${diff.counts.absents} absent(s)`}/>
                                        </>
                                    )}
                                </Stack>
                            </Box>
                            <Divider/>
                            <Box sx={{p: 2}}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                    <Box sx={{flex: 1}}>
                                        {parsed?.errors.length > 0 && (
                                            <Alert severity="error" sx={{mb: 1}}>
                                                <AlertTitle>{parsed.errors.length} erreur(s) — corriger le fichier puis réimporter</AlertTitle>
                                                <List dense disablePadding>
                                                    {parsed.errors.map((er, i) => (
                                                        <ListItem key={i} disableGutters sx={{py: 0}}>
                                                            <ListItemText primary={er.message}/>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Alert>
                                        )}
                                        {parsed?.warnings.length > 0 && (
                                            <Alert severity="info" sx={{mb: 1}}>{parsed.warnings.map((w) => w.message).join(' ')}</Alert>
                                        )}
                                        {parsed && !parsed.hasCoords && !hasBlockingErrors && (
                                            <Alert severity="info" sx={{mb: 1}}>Fichier sans colonnes Latitude/Longitude : coordonnées non comparées.</Alert>
                                        )}
                                        {diff && !hasBlockingErrors && notReconciled && (
                                            <Alert severity="error" sx={{mb: 1}}
                                                   action={
                                                       <Button color="inherit" size="small" startIcon={<Refresh/>}
                                                               onClick={() => refetchPatrimoine()} disabled={patrimoineFetching}>
                                                           Actualiser
                                                       </Button>
                                                   }>
                                                <AlertTitle>Validation impossible : le fichier et le patrimoine ne contiennent pas les mêmes bornes</AlertTitle>
                                                {diff.counts.absents > 0 && <div>{diff.counts.absents} absente(s) du fichier : {diff.absents.map((a) => a.conteneur.nomenclature).join(', ')}</div>}
                                                {diff.counts.unknown > 0 && <div>{diff.counts.unknown} nouvelle(s) dans le fichier : {diff.unknown.map((u) => u.row.nomenclature).join(', ')}</div>}
                                                <div>Régler dans le patrimoine (ou corriger le fichier), puis « Actualiser » pour recomparer sans réimporter.</div>
                                            </Alert>
                                        )}
                                        {diff && !hasBlockingErrors && !notReconciled && !diff.hasChanges && (
                                            <Alert severity="success" sx={{mb: 1}}>Référentiel conforme au fichier : aucun écart à appliquer.</Alert>
                                        )}
                                        {!parsed && (
                                            <Typography gutterBottom variant="subtitle1" mb={1}>Nouvel ordre</Typography>
                                        )}
                                        {fileName && <Chip label={fileName} size="small" onDelete={resetImport}/>}
                                    </Box>

                                    <form>
                                        <input
                                            id="csv-input"
                                            type="file"
                                            accept=".csv,text/csv,text/plain"
                                            ref={inputFileRef}
                                            style={{display: 'none'}}
                                            disabled={importDisabled}
                                            onChange={(event) => {
                                                const file = event.target.files?.[0];
                                                event.target.value = null; // permet de re-sélectionner le même fichier après correction
                                                handleFile(file);
                                            }}
                                        />
                                        <label htmlFor="csv-input">
                                            <Button variant="contained" component="span" disabled={importDisabled}>
                                                Importer
                                            </Button>
                                        </label>
                                    </form>
                                </Stack>

                                <List dense>
                                    {rightVisible.map((e) => (
                                        <ListItem key={e.row.line} divider sx={rightRowStyle(e)}
                                                  secondaryAction={
                                                      <Stack direction="row" spacing={.5}>
                                                          {e.status === 'unknown' && (
                                                              <Item bgcolor="var(--thm-base-light)" color="var(--thm-base-accent)">Nouveau</Item>
                                                          )}
                                                          {e.status !== 'unknown' && (e.ordre
                                                              ? <Tag kind="ordre" icon={<SwapVert fontSize="small"/>} label={`${e.ordre.from ?? '—'} → ${e.ordre.to}`} title={`Ordre ${e.ordre.from ?? '—'} → ${e.ordre.to}`}/>
                                                              : <TagSlot kind="ordre"/>)}
                                                          {e.status !== 'unknown' && (e.adresse
                                                              ? <Tag kind="adresse" icon={<EditLocationAlt fontSize="small"/>} title={`Adresse : ${e.adresse.from || '—'} → ${e.adresse.to}`}/>
                                                              : <TagSlot kind="adresse"/>)}
                                                          {e.status !== 'unknown' && (coordsVisible(e)
                                                              ? (e.coords.distanceM != null
                                                                  ? <Tag kind="coords" icon={<PinDrop fontSize="small"/>} label={helper.formatDistance(e.coords.distanceM)} title={`Déplacé de ${helper.formatDistance(e.coords.distanceM)} : ${fmtCoords(e.coords.from)} → ${fmtCoords(e.coords.to)}`}/>
                                                                  : <Tag kind="coords" icon={<AddLocationAlt fontSize="small"/>} label="GPS" title={`Coordonnées renseignées : ${fmtCoords(e.coords.to)}`}/>)
                                                              : <TagSlot kind="coords"/>)}
                                                      </Stack>
                                                  }>
                                            <ListItemText primary={formatLine(e.row.ordreCsv, e.row.nomenclature, e.row.adresse || e.conteneur?.adresse)}/>
                                        </ListItem>
                                    ))}
                                    {diff && rightVisible.length === 0 && (
                                        <ListItem><ListItemText secondary={onlyChanges ? 'Aucun écart.' : 'Aucune ligne.'}/></ListItem>
                                    )}
                                </List>
                            </Box>
                        </Card>
                    </Grid>

                    {/* ============================ Validation ============================ */}
                    <Grid size={12} container justifyContent="center" pt={2}>
                        <Grid size={{xs: 12, md: 4}}>
                            <Button variant="contained" fullWidth onClick={() => setConfirmOpen(true)} disabled={!canValidate}>
                                Valider
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            <Dialog open={confirmOpen} onClose={() => !isUpdating && setConfirmOpen(false)}>
                <DialogTitle>Mettre à jour la tournée {campaign?.libelle} ?</DialogTitle>
                <DialogContent>
                    {diff && (
                        <List dense>
                            <ListItem><ListItemText primary={`${diff.counts.changed} conteneur(s) modifié(s) sur ${diff.entries.length}`}/></ListItem>
                            <ListItem><ListItemText primary={`Nombre de changement d'ordre : ${diff.counts.ordre}`}/></ListItem>
                            <ListItem><ListItemText primary={`Nombre de changement d'adresse : ${diff.counts.adresse}`}/></ListItem>
                            <ListItem><ListItemText primary={`Nombre de changement de coordonnées : ${diff.counts.coords}`}/></ListItem>
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} variant="contained" color="error" disabled={isUpdating}>Annuler</Button>
                    <Button onClick={submit} variant="contained" disabled={isUpdating} autoFocus>
                        {isUpdating ? <CircularProgress size={22}/> : 'Mettre à jour le plan de tournée'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default CampaignUpdate;
